/**
 * Efectos del kit de celebraciones — port tipado de `celebrations.js`.
 *
 * Diferencia clave con el bundle original: el motor corre sobre
 * `requestAnimationFrame` (con acumulador de tiempo a 60fps) en vez de
 * `setInterval`. Motivos:
 *   - rAF se PAUSA cuando la pestaña está oculta → no dibuja ni quema batería
 *     en background (respuesta directa al riesgo de gasto de recursos).
 *   - El acumulador mantiene la física a 60fps independientemente del refresh
 *     del display (60/120/144 Hz) — sin esto, una pantalla de 120Hz correría
 *     el doble de rápido.
 *   - Tras volver de background, el catch-up se clampea a 4 frames para no
 *     disparar un bucle síncrono largo.
 *
 * Cada efecto se auto-limpia (remueve su capa del DOM) al terminar o al
 * llamar `stop()`. Toda partícula vive dentro de un canvas/capa que se
 * destruye con la capa: no hay estado retenido entre celebraciones.
 */

import type { CelebrationHandle, CelebrationOptions } from './types.js';
import { BRAND, GOLD, WARM } from './catalog.js';

// ─── Helpers numéricos ───────────────────────────────────────────────────
function rnd(a: number, b: number): number {
  return a + Math.random() * (b - a);
}

function pick<T>(arr: readonly T[]): T {
  const v = arr[(Math.random() * arr.length) | 0];
  // Fail-fast: pick sobre un array vacío es un bug de programación, no un caso
  // a degradar silenciosamente.
  if (v === undefined) throw new Error('[celebrations] pick() sobre array vacío');
  return v;
}

// ─── Capa + canvas ────────────────────────────────────────────────────────
/** Asegura que el contenedor pueda anclar una capa absoluta. */
function ensurePositioned(el: HTMLElement): void {
  if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
}

function makeLayer(opts: CelebrationOptions): HTMLDivElement {
  const host = opts.target ?? document.body;
  if (host !== document.body) ensurePositioned(host);
  const layer = document.createElement('div');
  layer.className = 'cel-layer';
  layer.style.cssText = `position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:${opts.z ?? 60};`;
  if (host === document.body) layer.style.position = 'fixed';
  host.appendChild(layer);
  return layer;
}

interface CanvasCtx {
  ctx: CanvasRenderingContext2D;
  W: number;
  H: number;
}

function makeCanvas(layer: HTMLElement): CanvasCtx {
  const r = layer.getBoundingClientRect();
  const W = Math.max(1, r.width);
  const H = Math.max(1, r.height);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(W * dpr);
  canvas.height = Math.round(H * dpr);
  canvas.style.cssText = 'width:100%;height:100%;display:block;';
  layer.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('[celebrations] no se pudo obtener el contexto 2d del canvas');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, W, H };
}

// ─── Bucle por frames sobre rAF ────────────────────────────────────────────
const STEP_MS = 1000 / 60;
const MAX_CATCHUP_FRAMES = 4;

/**
 * `fn(frame)` corre a 60fps lógicos. Devolver `false` termina antes de
 * `maxFrames`. `cleanup()` se llama una sola vez al terminar/cancelar.
 */
function runLoop(
  fn: (frame: number) => boolean | void,
  maxFrames: number,
  cleanup: () => void,
): CelebrationHandle {
  let frame = 0;
  let running = true;
  let rafId = 0;
  let last = 0;
  let acc = 0;

  function finish(): void {
    if (!running) return;
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    cleanup();
  }

  function tick(ts: number): void {
    if (!running) return;
    if (last === 0) last = ts;
    acc += ts - last;
    last = ts;
    // Clamp catch-up: si la pestaña estuvo oculta, no recuperamos N frames de
    // golpe — resumimos cerca de donde quedó.
    if (acc > STEP_MS * MAX_CATCHUP_FRAMES) acc = STEP_MS * MAX_CATCHUP_FRAMES;

    while (acc >= STEP_MS) {
      acc -= STEP_MS;
      const cont = fn(frame);
      frame++;
      if (cont === false || frame > maxFrames) {
        finish();
        return;
      }
    }
    rafId = requestAnimationFrame(tick);
  }

  rafId = requestAnimationFrame(tick);
  return { stop: finish };
}

// ─── Formas ────────────────────────────────────────────────────────────────
type Shape = 'rect' | 'circ' | 'spark';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  g: number;
  size: number;
  color: string;
  rot: number;
  vr: number;
  shape: Shape;
  life: number;
  max: number;
  sway: number;
  phase: number;
  alpha: number;
}

/** Destello de 4 puntas (sparkle), centrado en (0,0). */
function drawSpark(ctx: CanvasRenderingContext2D, s: number): void {
  const r = s;
  const w = s * 0.16;
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(w, w, 0, r);
    ctx.quadraticCurveTo(-w, w, 0, 0);
    ctx.rotate(Math.PI / 2);
  }
  ctx.fill();
}

function drawShape(ctx: CanvasRenderingContext2D, p: Particle): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.globalAlpha = p.alpha;
  ctx.fillStyle = p.color;
  if (p.shape === 'circ') {
    ctx.beginPath();
    ctx.arc(0, 0, p.size * 0.5, 0, Math.PI * 2);
    ctx.fill();
  } else if (p.shape === 'spark') {
    drawSpark(ctx, p.size);
  } else {
    ctx.fillRect(-p.size / 2, -p.size * 0.34, p.size, p.size * 0.68);
  }
  ctx.restore();
}

// ════════════════════════════════════════════════════════════════════════
//  Modo sobrio (prefers-reduced-motion): un destello breve, sin partículas
// ════════════════════════════════════════════════════════════════════════
export function sober(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  layer.style.transition = 'opacity .5s ease';
  layer.style.background = `radial-gradient(circle at 50% 45%, ${(opts.colors ?? BRAND)[0]}22, transparent 60%)`;
  layer.style.opacity = '0';
  requestAnimationFrame(() => {
    layer.style.opacity = '1';
  });
  let stopped = false;
  const t = window.setTimeout(() => {
    layer.style.opacity = '0';
    window.setTimeout(() => {
      if (layer.parentNode) layer.remove();
      opts.onDone?.();
    }, 500);
  }, 650);
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      window.clearTimeout(t);
      if (layer.parentNode) layer.remove();
    },
  };
}

function cleanupLayer(layer: HTMLElement, opts: CelebrationOptions): () => void {
  return () => {
    if (layer.parentNode) layer.remove();
    opts.onDone?.();
  };
}

// ════════════════════════════════════════════════════════════════════════
//  EFECTOS DE CANVAS
// ════════════════════════════════════════════════════════════════════════
export function confettiCannons(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  const { ctx, W, H } = makeCanvas(layer);
  const colors = opts.colors ?? BRAND;
  const sc = opts.scale ?? 1;
  const parts: Particle[] = [];

  function burst(ox: number, oy: number, base: number, n: number): void {
    for (let i = 0; i < n; i++) {
      const a = base + rnd(-0.42, 0.42);
      const sp = rnd(7.5, 15.5) * (Math.min(W, H) / 400 + 0.5) * sc;
      parts.push({
        x: ox,
        y: oy,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        g: rnd(0.16, 0.26),
        size: rnd(5, 11) * sc,
        color: pick(colors),
        rot: rnd(0, 6.28),
        vr: rnd(-0.32, 0.32),
        shape: Math.random() < 0.62 ? 'rect' : 'circ',
        life: 0,
        max: rnd(95, 150),
        sway: rnd(0.4, 1.6),
        phase: 0,
        alpha: 1,
      });
    }
  }
  burst(W * 0.07, H * 0.97, -Math.PI / 2 + 0.5, 80);
  burst(W * 0.93, H * 0.97, -Math.PI / 2 - 0.5, 80);
  for (let i = 0; i < 46; i++) {
    parts.push({
      x: rnd(0, W),
      y: rnd(-H * 0.4, -8),
      vx: rnd(-1.2, 1.2),
      vy: rnd(2, 4.5),
      g: rnd(0.04, 0.09),
      size: rnd(4, 9) * sc,
      color: pick(colors),
      rot: rnd(0, 6.28),
      vr: rnd(-0.25, 0.25),
      shape: Math.random() < 0.62 ? 'rect' : 'circ',
      life: 0,
      max: rnd(160, 250),
      sway: rnd(0.6, 2),
      phase: 0,
      alpha: 1,
    });
  }

  return runLoop(
    (f) => {
      ctx.clearRect(0, 0, W, H);
      let alive = 0;
      for (const p of parts) {
        if (p.life > p.max) continue;
        p.life++;
        p.vy += p.g;
        p.vx *= 0.992;
        p.x += p.vx + Math.sin((p.life + f) * 0.06) * p.sway * 0.35;
        p.y += p.vy;
        p.rot += p.vr;
        if (p.y - p.size > H + 40) continue;
        alive++;
        p.alpha = p.life > p.max - 34 ? Math.max(0, (p.max - p.life) / 34) : 1;
        drawShape(ctx, p);
      }
      return alive > 0;
    },
    480,
    cleanupLayer(layer, opts),
  );
}

export function confettiRain(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  const { ctx, W, H } = makeCanvas(layer);
  const colors = opts.colors ?? BRAND;
  const sc = opts.scale ?? 1;
  const parts: Particle[] = [];
  const N = Math.round(90 * (W / 360));
  for (let i = 0; i < N; i++) {
    parts.push({
      x: rnd(0, W),
      y: rnd(-H, -10),
      vx: rnd(-0.6, 0.6),
      vy: rnd(1.6, 3.6),
      g: 0.012,
      size: rnd(4, 9) * sc,
      color: pick(colors),
      rot: rnd(0, 6.28),
      vr: rnd(-0.18, 0.18),
      shape: Math.random() < 0.6 ? 'rect' : 'circ',
      life: 0,
      max: 99999,
      sway: rnd(0.8, 2.4),
      phase: rnd(0, 6.28),
      alpha: 1,
    });
  }
  const stopAt = 150;
  return runLoop(
    (f) => {
      ctx.clearRect(0, 0, W, H);
      const fade = f > stopAt ? Math.max(0, 1 - (f - stopAt) / 60) : 1;
      for (const p of parts) {
        p.life++;
        p.vy += p.g;
        p.x += Math.sin(p.phase + p.life * 0.05) * p.sway * 0.5;
        p.y += p.vy;
        p.rot += p.vr;
        if (p.y - p.size > H) {
          p.y = -10;
          p.x = rnd(0, W);
        }
        p.alpha = fade;
        drawShape(ctx, p);
      }
      return fade > 0;
    },
    230,
    cleanupLayer(layer, opts),
  );
}

interface Rocket {
  x: number;
  y: number;
  ty: number;
  vy: number;
  color: string;
}

export function fireworks(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  const { ctx, W, H } = makeCanvas(layer);
  const colors = opts.colors ?? BRAND;
  const sc = opts.scale ?? 1;
  const rockets: Rocket[] = [];
  const sparks: Particle[] = [];
  const shellCount = opts.count ?? 5;
  const schedule: { at: number; done: boolean }[] = [];
  for (let i = 0; i < shellCount; i++) schedule.push({ at: Math.round(rnd(0, 130)), done: false });

  function launch(): void {
    const tx = rnd(W * 0.2, W * 0.8);
    const ty = rnd(H * 0.18, H * 0.5);
    rockets.push({ x: tx, y: H, ty, vy: -rnd(7, 10), color: pick(colors) });
  }
  function explode(x: number, y: number, col: string): void {
    const n = Math.round(rnd(34, 54));
    const base = rnd(2.5, 5) * sc;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rnd(-0.1, 0.1);
      const sp = base * rnd(0.5, 1.1);
      sparks.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        g: 0.05,
        color: Math.random() < 0.25 ? '#ffffff' : col,
        size: rnd(2.2, 3.8) * sc,
        rot: 0,
        vr: 0,
        shape: 'circ',
        life: 0,
        max: rnd(45, 75),
        sway: 0,
        phase: 0,
        alpha: 1,
      });
    }
  }
  return runLoop(
    (f) => {
      ctx.clearRect(0, 0, W, H);
      for (const s of schedule) {
        if (!s.done && f >= s.at) {
          s.done = true;
          launch();
        }
      }
      for (let r = rockets.length - 1; r >= 0; r--) {
        const k = rockets[r];
        if (!k) continue;
        k.y += k.vy;
        k.vy += 0.06;
        ctx.globalAlpha = 1;
        ctx.fillStyle = k.color;
        ctx.beginPath();
        ctx.arc(k.x, k.y, 2.4 * sc, 0, 6.28);
        ctx.fill();
        ctx.globalAlpha = 0.4;
        ctx.fillRect(k.x - 1, k.y, 2, 10);
        if (k.y <= k.ty || k.vy >= -0.5) {
          explode(k.x, k.y, k.color);
          rockets.splice(r, 1);
        }
      }
      ctx.globalAlpha = 1;
      let alive = 0;
      for (const p of sparks) {
        if (p.life > p.max) continue;
        p.life++;
        p.vy += p.g;
        p.vx *= 0.985;
        p.vy *= 0.985;
        p.x += p.vx;
        p.y += p.vy;
        p.alpha = Math.max(0, 1 - p.life / p.max);
        alive++;
        drawShape(ctx, p);
      }
      const pending = schedule.some((x) => !x.done);
      return alive > 0 || rockets.length > 0 || pending;
    },
    360,
    cleanupLayer(layer, opts),
  );
}

interface Star {
  x: number;
  y: number;
  max: number;
  color: string;
  born: number;
  dur: number;
  rot: number;
  spin: number;
  life: number;
}

export function sparkles(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  const { ctx, W, H } = makeCanvas(layer);
  const colors = opts.colors ?? ['#d4f23e', '#a78bfa', '#ffffff', '#ffd166'];
  const sc = opts.scale ?? 1;
  const stars: Star[] = [];
  const N = Math.round(opts.count ?? 26);
  for (let i = 0; i < N; i++) {
    stars.push({
      x: rnd(W * 0.1, W * 0.9),
      y: rnd(H * 0.12, H * 0.9),
      max: rnd(14, 26) * sc,
      color: pick(colors),
      born: Math.round(rnd(0, 70)),
      dur: Math.round(rnd(26, 46)),
      rot: rnd(-0.4, 0.4),
      spin: rnd(-0.05, 0.05),
      life: 0,
    });
  }
  return runLoop(
    (f) => {
      ctx.clearRect(0, 0, W, H);
      let alive = 0;
      for (const s of stars) {
        if (f < s.born) {
          alive++;
          continue;
        }
        s.life++;
        if (s.life > s.dur) continue;
        alive++;
        const t = s.life / s.dur;
        const grow = t < 0.4 ? t / 0.4 : 1;
        const fade = t > 0.55 ? 1 - (t - 0.55) / 0.45 : 1;
        const size = s.max * grow;
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.rotate(s.rot + s.life * s.spin);
        ctx.globalAlpha = Math.max(0, fade);
        ctx.fillStyle = s.color;
        ctx.shadowColor = s.color;
        ctx.shadowBlur = 8;
        drawSpark(ctx, size);
        ctx.restore();
      }
      return alive > 0;
    },
    130,
    cleanupLayer(layer, opts),
  );
}

export function streak(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  const { ctx, W, H } = makeCanvas(layer);
  const colors = opts.colors ?? WARM;
  const sc = opts.scale ?? 1;
  const embers: Particle[] = [];
  const emitFor = 90;
  function spawn(): void {
    embers.push({
      x: rnd(W * 0.12, W * 0.88),
      y: H + 6,
      vx: rnd(-0.5, 0.5),
      vy: -rnd(1.6, 3.6) * sc,
      g: 0,
      size: rnd(4, 10) * sc,
      color: pick(colors),
      rot: 0,
      vr: 0,
      shape: Math.random() < 0.5 ? 'circ' : 'rect',
      life: 0,
      max: rnd(50, 90),
      sway: rnd(0.4, 1.4),
      phase: rnd(0, 6.28),
      alpha: 1,
    });
  }
  return runLoop(
    (f) => {
      ctx.clearRect(0, 0, W, H);
      if (f < emitFor) for (let n = 0; n < 4; n++) spawn();
      let alive = 0;
      ctx.globalCompositeOperation = 'lighter';
      for (const p of embers) {
        if (p.life > p.max) continue;
        p.life++;
        p.vy *= 0.985;
        p.x += Math.sin(p.phase + p.life * 0.08) * p.sway;
        p.y += p.vy;
        const t = p.life / p.max;
        p.alpha = (t < 0.2 ? t / 0.2 : 1 - (t - 0.2) / 0.8) * 0.9;
        p.size *= 0.992;
        alive++;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(0, 0, p.size * 0.5, 0, 6.28);
        ctx.fill();
        ctx.restore();
      }
      ctx.globalCompositeOperation = 'source-over';
      return alive > 0 || f < emitFor;
    },
    200,
    cleanupLayer(layer, opts),
  );
}

export function levelUp(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  const { ctx, W, H } = makeCanvas(layer);
  const colors = opts.colors ?? BRAND;
  const sc = opts.scale ?? 1;
  const cx = W / 2;
  const cy = H * 0.52;
  const streams: Particle[] = [];
  for (let i = 0; i < 60; i++) {
    const a = rnd(-Math.PI * 0.75, -Math.PI * 0.25);
    const sp = rnd(3, 8) * sc;
    streams.push({
      x: cx + rnd(-20, 20),
      y: cy,
      vx: Math.cos(a) * sp * 0.5,
      vy: Math.sin(a) * sp,
      g: 0.08,
      size: rnd(4, 9) * sc,
      color: pick(colors),
      rot: rnd(0, 6.28),
      vr: rnd(-0.3, 0.3),
      shape: Math.random() < 0.6 ? 'rect' : 'circ',
      life: 0,
      max: rnd(50, 90),
      sway: rnd(0.3, 1),
      phase: 0,
      alpha: 1,
    });
  }
  const ring = colors[0] ?? '#d4f23e';
  return runLoop(
    (f) => {
      ctx.clearRect(0, 0, W, H);
      if (f < 40) {
        const rt = f / 40;
        const R = rt * Math.min(W, H) * 0.55;
        ctx.save();
        ctx.globalAlpha = (1 - rt) * 0.9;
        ctx.strokeStyle = ring;
        ctx.lineWidth = 4 * (1 - rt) + 1;
        ctx.shadowColor = ring;
        ctx.shadowBlur = 18;
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, 6.28);
        ctx.stroke();
        ctx.restore();
      }
      if (f < 16) {
        const ft = 1 - f / 16;
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 90 * sc);
        g.addColorStop(0, `rgba(255,255,255,${ft * 0.8})`);
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      }
      let alive = 0;
      for (const p of streams) {
        if (p.life > p.max) continue;
        p.life++;
        p.vy += p.g;
        p.x += p.vx + Math.sin(p.life * 0.1) * p.sway;
        p.y += p.vy;
        p.rot += p.vr;
        p.alpha = p.life > p.max - 24 ? Math.max(0, (p.max - p.life) / 24) : 1;
        alive++;
        drawShape(ctx, p);
      }
      return alive > 0 || f < 40;
    },
    130,
    cleanupLayer(layer, opts),
  );
}

// ════════════════════════════════════════════════════════════════════════
//  EFECTOS DE DOM
// ════════════════════════════════════════════════════════════════════════
interface EmojiItem {
  el: HTMLDivElement;
  x: number;
  y: number;
  vy: number;
  drift: number;
  rot: number;
  vr: number;
  life: number;
  max: number;
}

export function emojiFloat(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  const r = layer.getBoundingClientRect();
  const W = Math.max(1, r.width);
  const H = Math.max(1, r.height);
  const emojis = opts.emojis ?? ['🎉', '✨', '💪', '🔥', '🌟', '👏', '🚀'];
  const count = opts.count ?? 16;
  const sc = opts.scale ?? 1;
  const items: (EmojiItem | null)[] = [];
  let spawned = 0;
  const every = 5;
  function spawn(): void {
    const el = document.createElement('div');
    el.textContent = pick(emojis);
    const size = rnd(22, 40) * sc;
    el.style.cssText = `position:absolute;left:0;top:0;font-size:${size}px;line-height:1;will-change:transform,opacity;filter:drop-shadow(0 4px 8px rgba(0,0,0,.4));`;
    layer.appendChild(el);
    items.push({
      el,
      x: rnd(W * 0.1, W * 0.9),
      y: H + 10,
      vy: -rnd(1.3, 2.4),
      drift: rnd(-0.5, 0.5),
      rot: rnd(-26, 26),
      vr: rnd(-1.2, 1.2),
      life: 0,
      max: Math.round(rnd(95, 150)),
    });
  }
  return runLoop(
    (f) => {
      if (spawned < count && f % every === 0) {
        spawn();
        spawned++;
      }
      let alive = 0;
      for (let k = 0; k < items.length; k++) {
        const it = items[k];
        if (!it) continue;
        if (it.life > it.max) {
          if (it.el.parentNode) it.el.remove();
          items[k] = null;
          continue;
        }
        it.life++;
        it.y += it.vy;
        it.x += it.drift;
        it.rot += it.vr;
        const t = it.life / it.max;
        const op = t < 0.12 ? t / 0.12 : t > 0.75 ? Math.max(0, 1 - (t - 0.75) / 0.25) : 1;
        it.el.style.opacity = String(op);
        it.el.style.transform = `translate(${it.x}px,${it.y}px) rotate(${it.rot}deg)`;
        alive++;
      }
      return alive > 0 || spawned < count;
    },
    220,
    cleanupLayer(layer, opts),
  );
}

export function achievement(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  layer.style.display = 'flex';
  layer.style.alignItems = 'flex-start';
  layer.style.justifyContent = 'center';
  const card = document.createElement('div');
  card.className = 'cel-toast';
  const emoji = opts.emoji ?? '🏆';
  const title = opts.title ?? '¡Logro desbloqueado!';
  const subtitle = opts.subtitle ?? 'Seguí así';
  card.innerHTML =
    `<span class="cel-toast-badge">${emoji}</span>` +
    `<span class="cel-toast-text"><b>${title}</b><span>${subtitle}</span></span>` +
    `<span class="cel-toast-spark">✦</span>`;
  card.style.opacity = '0';
  layer.appendChild(card);
  const inner = sparkles({ target: layer, count: 12, scale: 0.7, z: 1 });
  const total = Math.round((opts.duration ?? 2600) / STEP_MS);
  const inF = 11;
  const outF = 16;
  const handle = runLoop(
    (f) => {
      let op: number;
      let ty: number;
      let s: number;
      if (f < inF) {
        const t = f / inF;
        op = t;
        s = 0.92 + 0.08 * t;
        ty = -26 * (1 - t);
      } else if (f > total - outF) {
        const t2 = Math.max(0, (total - f) / outF);
        op = t2;
        s = 0.98 + 0.02 * t2;
        ty = -14 * (1 - t2);
      } else {
        op = 1;
        s = 1;
        ty = 0;
      }
      card.style.opacity = String(op);
      card.style.transform = `translateY(${ty}px) scale(${s})`;
      return f <= total;
    },
    total + 2,
    cleanupLayer(layer, opts),
  );
  // Combinamos el control del toast con el de los destellos internos.
  return {
    stop: () => {
      inner.stop();
      handle.stop();
    },
  };
}

export function successPulse(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  layer.style.display = 'flex';
  layer.style.alignItems = 'center';
  layer.style.justifyContent = 'center';
  const color = (opts.colors ?? ['#d4f23e'])[0] ?? '#d4f23e';
  const wrap = document.createElement('div');
  wrap.className = 'cel-pulse';
  wrap.innerHTML =
    `<span class="cel-pulse-ring" style="border-color:${color}"></span>` +
    `<span class="cel-pulse-ring d2" style="border-color:${color}"></span>` +
    `<span class="cel-pulse-core" style="background:${color}">` +
    '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#0c0d15" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12 L 10 17 L 19 7"/></svg>' +
    '</span>';
  layer.appendChild(wrap);
  const core = wrap.querySelector<HTMLElement>('.cel-pulse-core');
  const rings = wrap.querySelectorAll<HTMLElement>('.cel-pulse-ring');
  if (core) core.style.transform = 'scale(0)';
  const total = 66;
  return runLoop(
    (f) => {
      const ct = Math.min(1, f / 16);
      const cs = ct < 0.6 ? (ct / 0.6) * 1.12 : 1.12 - ((ct - 0.6) / 0.4) * 0.12;
      if (core) core.style.transform = `scale(${f <= 16 ? cs : 1})`;
      rings.forEach((rgEl, i) => {
        const rt = (f - i * 11) / 42;
        if (rt < 0 || rt > 1) {
          rgEl.style.opacity = '0';
          return;
        }
        rgEl.style.transform = `scale(${0.7 + rt * 1.7})`;
        rgEl.style.opacity = String(0.85 * (1 - rt));
      });
      return f <= total;
    },
    total + 2,
    cleanupLayer(layer, opts),
  );
}

// ════════════════════════════════════════════════════════════════════════
//  FESTEJO BONUS — un escalón sobre `confetti-cannons`
// ════════════════════════════════════════════════════════════════════════
//
// El objetivo bonus de un escenario cumplido (encargo 104; Emmanuel eligió la
// lluvia dorada entre tres opciones el 15/10). Usa `GOLD` (dorado/coral/blanco)
// para separarse del confeti de marca (lima/violeta) que ya festeja los
// objetivos principales, sin `shadowBlur` grande ni colores neón. Bajo
// reduced-motion cae a `bonusSober` (ver `REDUCED_VARIANT` en engine.ts).

const BONUS_TITLE = '¡Bonus!';
const BONUS_SUBTITLE = 'Objetivo extra cumplido';

let medalSeq = 0;

/** SVG de la medalla dorada (roseta con cintas coral). Ids únicos por instancia. */
function medalSvg(size: number): string {
  const id = `cel-bm-${++medalSeq}`;
  const star: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? 25 : 10.5;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    star.push(`${(60 + Math.cos(a) * r).toFixed(2)},${(62 + Math.sin(a) * r).toFixed(2)}`);
  }
  return (
    `<svg viewBox="0 0 120 150" width="${size}" height="${Math.round(size * 1.25)}" aria-hidden="true">` +
    '<defs>' +
    `<radialGradient id="${id}-face" cx="38%" cy="32%" r="78%">` +
    '<stop offset="0" stop-color="#fff2c8"/><stop offset=".5" stop-color="#f6c75a"/>' +
    '<stop offset="1" stop-color="#c98a2e"/></radialGradient>' +
    `<linearGradient id="${id}-rim" x1="0" y1="0" x2="1" y2="1">` +
    '<stop offset="0" stop-color="#ffe9a8"/><stop offset=".55" stop-color="#e3a43f"/>' +
    '<stop offset="1" stop-color="#a8681c"/></linearGradient>' +
    '</defs>' +
    // Cintas (detrás del disco).
    '<path d="M38 92 L24 146 L40 136 L50 148 L60 100 Z" fill="#e8825a"/>' +
    '<path d="M82 92 L96 146 L80 136 L70 148 L60 100 Z" fill="#ff9d6c"/>' +
    // Disco: canto + cara + filete interior.
    `<circle cx="60" cy="62" r="56" fill="url(#${id}-rim)"/>` +
    `<circle cx="60" cy="62" r="46" fill="url(#${id}-face)"/>` +
    '<circle cx="60" cy="62" r="46" fill="none" stroke="rgba(122,72,14,.35)" stroke-width="1.5"/>' +
    '<circle cx="60" cy="62" r="40" fill="none" stroke="rgba(255,244,210,.45)" stroke-width="1" stroke-dasharray="2 4"/>' +
    `<polygon points="${star.join(' ')}" fill="#fff7e2" stroke="rgba(150,92,20,.5)" stroke-width="1.5" stroke-linejoin="round"/>` +
    '</svg>'
  );
}

/** Monta medalla + título + subtítulo centrados en `cy`. Devuelve los nodos. */
function mountMedal(
  layer: HTMLElement,
  opts: CelebrationOptions,
  cy: number,
  size: number,
): { medal: HTMLDivElement; shine: HTMLDivElement; label: HTMLDivElement } {
  const medal = document.createElement('div');
  medal.className = 'cel-bonus-medal';
  medal.style.cssText = `left:50%;top:${cy}px;width:${size}px;height:${Math.round(size * 1.25)}px;margin-left:${-size / 2}px;margin-top:${-size * 0.52}px;`;
  medal.innerHTML = medalSvg(size);
  const shine = document.createElement('div');
  shine.className = 'cel-bonus-shine';
  shine.style.cssText = `width:${size * 0.77}px;height:${size * 0.77}px;left:${size * 0.115}px;top:${size * 0.132}px;`;
  shine.innerHTML = '<span></span>';
  medal.appendChild(shine);
  const label = document.createElement('div');
  label.className = 'cel-bonus-label';
  label.style.top = `${cy + size * 0.8}px`;
  const b = document.createElement('b');
  b.textContent = opts.title ?? BONUS_TITLE;
  const sp = document.createElement('span');
  sp.textContent = opts.subtitle ?? BONUS_SUBTITLE;
  label.append(b, sp);
  layer.append(medal, label);
  return { medal, shine, label };
}

// ─── Lluvia dorada densa ──────────────────────────────────────────────────
type ConfettoKind = 'rect' | 'ribbon' | 'circ' | 'spark';

interface Confetto {
  x: number;
  y: number;
  vx: number;
  vy: number;
  g: number;
  /** Velocidad terminal de caída: el papel flota, no se desploma. */
  vmax: number;
  size: number;
  color: string;
  rot: number;
  vr: number;
  /** Fase del giro 3D (el papelito se da vuelta mientras cae). */
  flip: number;
  vflip: number;
  kind: ConfettoKind;
  sway: number;
  phase: number;
}

function drawConfetto(ctx: CanvasRenderingContext2D, p: Confetto, alpha: number): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = p.color;
  if (p.kind === 'spark') {
    drawSpark(ctx, p.size * 0.9);
  } else {
    // Escala vertical por el coseno del giro → el papel "se da vuelta".
    ctx.scale(1, Math.max(0.08, Math.abs(Math.cos(p.flip))));
    if (p.kind === 'circ') {
      ctx.beginPath();
      ctx.arc(0, 0, p.size * 0.45, 0, Math.PI * 2);
      ctx.fill();
    } else if (p.kind === 'ribbon') {
      ctx.fillRect(-p.size * 0.18, -p.size, p.size * 0.36, p.size * 2);
    } else {
      ctx.fillRect(-p.size / 2, -p.size * 0.32, p.size, p.size * 0.64);
    }
  }
  ctx.restore();
}

export function bonusRain(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  const { ctx, W, H } = makeCanvas(layer);
  const colors = opts.colors ?? GOLD;
  const sc = opts.scale ?? 1;
  const parts: Confetto[] = [];
  const widthK = W / 390;

  function kind(): ConfettoKind {
    const r = Math.random();
    return r < 0.45 ? 'rect' : r < 0.7 ? 'ribbon' : r < 0.9 ? 'circ' : 'spark';
  }
  function cannon(ox: number, oy: number, base: number, n: number, power: number): void {
    for (let i = 0; i < n; i++) {
      const a = base + rnd(-0.4, 0.4);
      const sp = rnd(8, 16) * power * (Math.min(W, H) / 400 + 0.5) * sc;
      parts.push({
        x: ox,
        y: oy,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        g: rnd(0.17, 0.25),
        vmax: rnd(2.6, 4.2),
        size: rnd(6, 12) * sc,
        color: pick(colors),
        rot: rnd(0, 6.28),
        vr: rnd(-0.25, 0.25),
        flip: rnd(0, 6.28),
        vflip: rnd(0.08, 0.22),
        kind: kind(),
        sway: rnd(0.4, 1.4),
        phase: rnd(0, 6.28),
      });
    }
  }
  function rainDrop(): void {
    parts.push({
      x: rnd(-10, W + 10),
      y: rnd(-60, -10),
      vx: rnd(-0.8, 0.8),
      vy: rnd(1.6, 3.2),
      g: 0.03,
      vmax: rnd(2.4, 4),
      size: rnd(6, 12) * sc,
      color: pick(colors),
      rot: rnd(0, 6.28),
      vr: rnd(-0.16, 0.16),
      flip: rnd(0, 6.28),
      vflip: rnd(0.06, 0.18),
      kind: kind(),
      sway: rnd(0.6, 2),
      phase: rnd(0, 6.28),
    });
  }

  // Telón inicial ya cayendo, para que la pantalla se llene desde el frame 1.
  for (let i = 0; i < Math.round(70 * widthK); i++) {
    rainDrop();
    const last = parts[parts.length - 1];
    if (last) last.y = rnd(-H * 0.45, -10);
  }
  const RAIN_UNTIL = 130;
  const FADE_AT = 172;
  const END = 214;

  return runLoop(
    (f) => {
      if (f === 0) {
        cannon(W * 0.04, H * 0.98, -Math.PI / 2 + 0.48, 110, 1);
        cannon(W * 0.96, H * 0.98, -Math.PI / 2 - 0.48, 110, 1);
      }
      // El par extra: desde los costados, a media altura, un poco después.
      if (f === 16) {
        cannon(-4, H * 0.62, -Math.PI / 4, 70, 0.85);
        cannon(W + 4, H * 0.62, (-3 * Math.PI) / 4, 70, 0.85);
      }
      if (f < RAIN_UNTIL) {
        const n = Math.max(1, Math.round(3.2 * widthK));
        for (let i = 0; i < n; i++) rainDrop();
      }
      const fade = f > FADE_AT ? Math.max(0, 1 - (f - FADE_AT) / (END - FADE_AT)) : 1;
      ctx.clearRect(0, 0, W, H);
      let write = 0;
      for (const p of parts) {
        p.vx *= 0.985;
        p.vy = Math.min(p.vy + p.g, p.vmax);
        p.x += p.vx + Math.sin(p.phase + f * 0.07) * p.sway * 0.45;
        p.y += p.vy;
        p.rot += p.vr;
        p.flip += p.vflip;
        if (p.y - p.size > H + 20) continue; // fuera de pantalla: se descarta
        parts[write++] = p;
        drawConfetto(ctx, p, fade);
      }
      parts.length = write;
      return f < END;
    },
    END + 2,
    cleanupLayer(layer, opts),
  );
}

// ─── Reduced-motion: medalla quieta + tinte dorado ─────────────────────────
/**
 * Variante sobria de los tres festejos bonus: sin desplazamientos ni
 * partículas — un tinte dorado y la medalla con «¡Bonus!» aparecen y se van
 * sólo por opacidad. Dura ~2,4 s para que se llegue a leer.
 */
export function bonusSober(opts: CelebrationOptions): CelebrationHandle {
  const layer = makeLayer(opts);
  const r = layer.getBoundingClientRect();
  const W = Math.max(1, r.width);
  const H = Math.max(1, r.height);
  const size = Math.round(Math.min(132, W * 0.34) * (opts.scale ?? 1));
  layer.style.background =
    'radial-gradient(circle at 50% 40%, rgba(255,209,102,.2), rgba(6,7,12,.35) 70%)';
  layer.style.transition = 'opacity .35s ease';
  layer.style.opacity = '0';
  mountMedal(layer, opts, H * 0.4, size);
  requestAnimationFrame(() => {
    layer.style.opacity = '1';
  });
  let stopped = false;
  let t2 = 0;
  const t1 = window.setTimeout(() => {
    layer.style.opacity = '0';
    t2 = window.setTimeout(() => {
      if (layer.parentNode) layer.remove();
      opts.onDone?.();
    }, 400);
  }, 2000);
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      if (layer.parentNode) layer.remove();
    },
  };
}
